/**
 * Where to put a player who is coming back.
 *
 * PRACTICE ONLY. A wagered match never respawns anyone — `world.ts` sets
 * `phase = "dead"`, assigns a placement and emits `eliminated`, and that is the
 * whole game. This exists for the gym and the scrim, which are for reps.
 *
 * THREE RULES, IN THIS ORDER, AND THE ORDER IS THE POINT.
 *
 * 1. RANDOM, so you do not learn one pad. Respawn used to index `map.spawns` by
 *    player index (gym) or `joinOrder` (scrim), so you returned to the same
 *    corner every time and practised the same approach every time.
 *
 * 2. INSIDE THE ZONE. A random pad in a closing arena is a pad that may already
 *    be outside the wall, and respawning into zone damage is worse than
 *    respawning predictably: you die again before you can move, with nothing on
 *    screen explaining why.
 *
 * 3. AWAY FROM THE LIVING. Landing on top of whoever just killed you is not a
 *    respawn, it is the same death again.
 *
 * Each rule is a PREFERENCE, not a filter. If nothing satisfies it the search
 * widens rather than failing — a respawn still has to put the player somewhere,
 * and somewhere imperfect beats nowhere.
 *
 * Uses the world RNG, never `Math.random`. `rng.ts` is explicit that a
 * `Math.random()` inside a step would make the seed commitment published at
 * lobby lock a decoration. Nothing verifies a scrim replay today; a respawn
 * that quietly broke determinism would be found by the thing that does.
 */
import { shuffled } from "./rng.js";
import { zoneRadiusAtTick } from "./zone.js";
/** How much room a returning player should get, in metres. */
export const MIN_RESPAWN_CLEARANCE = 12;
/** Half a body's width of margin, so a pad ON the wall does not count as inside it. */
const WALL_MARGIN = 2;
const ORIGIN = { x: 0, y: 6, z: 0 };
export function pickRespawn(world, player) {
    const spawns = world.map.spawns;
    if (spawns.length === 0)
        return ORIGIN;
    // Shuffled FIRST, so every step below picks from a randomly ordered set and
    // the result is genuinely random rather than "the first pad that qualifies".
    const order = shuffled(world, spawns);
    const radius = world.zone.suppressed
        ? Number.POSITIVE_INFINITY
        : zoneRadiusAtTick(world.tick, world.zone.startRadius);
    const insideZone = (s) => {
        if (!Number.isFinite(radius))
            return true;
        const dx = s.x - world.zone.centerX;
        const dz = s.z - world.zone.centerZ;
        return Math.sqrt(dx * dx + dz * dz) <= Math.max(0, radius - WALL_MARGIN);
    };
    const nearestLiving = (s) => {
        let best = Number.POSITIVE_INFINITY;
        for (const p of world.players) {
            if (p.id === player.id || p.phase !== "alive")
                continue;
            const dx = p.x - s.x;
            const dz = p.z - s.z;
            const d = Math.sqrt(dx * dx + dz * dz);
            if (d < best)
                best = d;
        }
        return best;
    };
    const safe = order.filter(insideZone);
    const pool = safe.length > 0 ? safe : order;
    const clear = pool.find((s) => nearestLiving(s) >= MIN_RESPAWN_CLEARANCE);
    if (clear)
        return clear;
    // Nobody is clear: a small arena late in a round. Take the roomiest pad
    // rather than the first, so the player gets the best of a bad set.
    let best = pool[0] ?? ORIGIN;
    let bestGap = -1;
    for (const s of pool) {
        const gap = nearestLiving(s);
        if (gap > bestGap) {
            bestGap = gap;
            best = s;
        }
    }
    return best;
}
//# sourceMappingURL=respawn.js.map